/**
 * Presentation dictionary (audit §6): one place that turns contract vocabulary into words a first-time user can act
 * on. Raw values never disappear: callers keep them in `title`, `data-*` and screen-reader text, and every lookup
 * falls back to the raw string for values this dictionary does not know.
 */
import type {
  ApprovalStatus,
  AutonomyClass,
  ErrorCode,
  SideEffectClass,
  TaskState,
} from '@agentic/contracts';

const TASK_STATE_LABELS: Readonly<Record<TaskState, string>> = {
  queued: 'Waiting to start',
  running: 'In progress',
  awaiting_input: 'Input needed',
  awaiting_approval: 'Approval needed',
  blocked: 'Blocked',
  reconciling: 'Checking the result',
  compensating: 'Recovering from a change',
  succeeded: 'Finished',
  failed: 'Could not finish',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

function lookup<K extends string>(table: Readonly<Record<K, string>>, value: string): string {
  return Object.hasOwn(table, value) ? table[value as K] : value;
}

/** Short user label for a task state, e.g. `awaiting_approval` → "Approval needed". */
export function taskStateLabel(state: TaskState | string): string {
  return lookup(TASK_STATE_LABELS, state);
}

export interface TaskStateContext {
  /** The viewer holds an approver role. */
  isApprover?: boolean;
  /** The viewer requested this task. */
  isRequester?: boolean;
}

/**
 * One sentence on what the state means and who acts next. It names a role only when the viewer's own role makes
 * that true, and never promises an outcome the platform has not verified.
 */
export function taskStateExplanation(state: TaskState | string, ctx: TaskStateContext = {}): string {
  switch (state) {
    case 'queued':
      return 'The agent run is waiting for a worker to start it.';
    case 'running':
      return 'The agent is working on the agent run. Nothing needs you yet.';
    case 'awaiting_input':
      return ctx.isRequester
        ? 'The agent needs more information from you before it can continue.'
        : 'The agent needs more information from the requester before it can continue.';
    case 'awaiting_approval':
      if (ctx.isRequester)
        return 'Waiting for another approver. You started this case, so you cannot approve it.';
      return ctx.isApprover
        ? 'Waiting for an approver. Review the proposed actions before you decide.'
        : 'Waiting for an approver to review the proposed actions.';
    case 'blocked':
      return 'The agent run cannot continue until the blocking problem is resolved. Check the activity for the reason.';
    case 'reconciling':
      return 'The platform is checking whether an action took effect. The outcome may be unknown; do not retry it manually.';
    case 'compensating':
      return 'The platform is attempting to correct an earlier change. A full reversal is not guaranteed.';
    case 'succeeded':
      return 'The agent run finished. Check the outcome and receipts for what actually changed.';
    case 'failed':
      return 'The agent run could not finish. Actions that already completed are not undone.';
    case 'cancelled':
      return 'The agent run was cancelled. Actions that already completed are not undone.';
    case 'expired':
      return 'The agent run expired before it finished. Create a new request if the work is still needed.';
    default:
      return `State: ${state}.`;
  }
}

const APPROVAL_STATUS_LABELS: Readonly<Record<ApprovalStatus, string>> = {
  pending: 'Waiting for decision',
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
  invalidated: 'Changes need a new review',
};

/** User label for an approval status, e.g. `invalidated` → "Changes need a new review". */
export function approvalStatusLabel(status: ApprovalStatus | string): string {
  return lookup(APPROVAL_STATUS_LABELS, status);
}

const SIDE_EFFECT_LABELS: Readonly<Record<SideEffectClass, string>> = {
  read: 'Read only',
  reversible_write: 'Can be undone',
  irreversible_write: 'Cannot be automatically undone',
  external_communication: 'Sends to someone outside',
};

/** User label for a side-effect class, e.g. `irreversible_write` → "Cannot be automatically undone". */
export function sideEffectLabel(value: SideEffectClass | string): string {
  return lookup(SIDE_EFFECT_LABELS, value);
}

const AUTONOMY_LABELS: Readonly<Record<AutonomyClass, string>> = {
  A0: 'Information only',
  A1: 'Prepares drafts',
  A2: 'Acts within limits',
  A3: 'Requires approval',
  A4: 'Restricted',
};

/** User label for an autonomy class, e.g. `A3` → "Requires approval". The class stays in detail views. */
export function autonomyLabel(value: AutonomyClass | string): string {
  return lookup(AUTONOMY_LABELS, value);
}

export interface ErrorExplanation {
  /** What went wrong, in one short sentence. */
  problem: string;
  /** What that means for the user's work. */
  effect: string;
  /** What the user can do now. */
  next: string;
}

const ERRORS: Readonly<Record<ErrorCode, ErrorExplanation>> = {
  validation_failed: {
    problem: 'Some information was missing or not in the expected format.',
    effect: 'Nothing was saved.',
    next: 'Check the highlighted fields and try again.',
  },
  unauthorized: {
    problem: 'Your session has ended.',
    effect: 'The request was not sent.',
    next: 'Sign in again, then repeat the action.',
  },
  forbidden: {
    problem: 'You are not allowed to do this.',
    effect: 'Nothing was changed.',
    next: 'Ask someone with the right role, or check the reason below.',
  },
  policy_denied: {
    problem: 'A policy does not allow this action.',
    effect: 'The action was not carried out.',
    next: 'Read the policy reason below; an operator can explain which rule applied.',
  },
  approval_required: {
    problem: 'This action needs an approval first.',
    effect: 'The action was not carried out.',
    next: 'Wait for the approval, or ask an approver to review it.',
  },
  approval_invalid: {
    problem: 'The approval no longer matches the proposed action.',
    effect: 'The action was not carried out.',
    next: 'The changes need a new review before anything runs.',
  },
  idempotency_conflict: {
    problem: 'A request with the same key was already sent with different details.',
    effect: 'No duplicate was created.',
    next: 'Open the existing case, or start a new request.',
  },
  not_found: {
    problem: 'This item could not be found.',
    effect: 'It may have been removed, or you may not have access to it.',
    next: 'Go back to the list and open it again.',
  },
  conflict: {
    problem: 'Something changed while you were working.',
    effect: 'Your change was not applied.',
    next: 'Reload to see the current state, then try again.',
  },
  timeout: {
    problem: 'The platform took too long to answer.',
    effect: 'It is not known whether the request took effect.',
    next: 'Reload to check the current state before you try again.',
  },
  downstream_failed: {
    problem: 'A connected system did not respond correctly.',
    effect: 'Your request may not have finished.',
    next: 'Check the current status before trying again. If it keeps failing, tell an operator.',
  },
  outcome_unknown: {
    problem: 'It is not known whether the action took effect.',
    effect: 'The platform will check the result; do not repeat the action manually.',
    next: 'Watch the agent run for the reconciliation result.',
  },
  limit_exceeded: {
    problem: 'A usage limit was reached.',
    effect: 'The request was not carried out.',
    next: 'Wait a moment and try again, or ask an operator to review the limit.',
  },
  kill_switch: {
    problem: 'An operator has paused this agent or tool.',
    effect: 'New work for it is not accepted.',
    next: 'Choose another capability, or ask an operator when it will be resumed.',
  },
  internal: {
    problem: 'Something went wrong on our side.',
    effect: 'The request may not have finished.',
    next: 'Try again. If it keeps failing, give the reference below to an operator.',
  },
  step_up_required: {
    problem: 'This step needs a stronger sign-in.',
    effect: 'Nothing was changed.',
    next: 'Sign in again with your company SSO (with the second factor), then repeat the action.',
  },
  workflow_version_missing: {
    problem: 'This agent run was started on a workflow version that no worker runs any more.',
    effect: 'The agent run stopped; nothing more was done for it.',
    next: 'Start the agent run again, or ask an operator to deploy a worker with that workflow version.',
  },
  account_setup_required: {
    problem: 'Your account needs a one-time setup before you can sign in with a password.',
    effect: 'You are not signed in.',
    next: 'Use Sign in with company SSO to set your password, then sign in again.',
  },
  evaluation_name_conflict: {
    problem: 'Two evaluations claim the same name in the evaluation catalog.',
    effect: 'The platform did not start with the conflicting pack evaluations.',
    next: 'Rename the pack evaluation in its module.yaml (provides.evaluations), then restart.',
  },
  tool_out_of_scope: {
    problem: 'The step tried to use a tool that its process activity does not allow.',
    effect: 'Nothing was changed. The attempt is recorded in the action ledger.',
    next: 'Ask the process owner to add the tool to the activity, or use an allowed tool.',
  },
  recovery_effects_incomplete: {
    problem: 'The recovery decision does not say what to do with every completed effect.',
    effect: 'The process did not go back to the earlier step.',
    next: 'Decide "stand" or "compensate" for each completed effect, then try again.',
  },
  compensation_unavailable: {
    problem: 'This effect cannot be undone automatically: its tool has no compensation.',
    effect: 'The effect stands. A "manual reversal" obligation is opened for the activity owner.',
    next: 'Reverse the effect by hand and close the obligation with evidence.',
  },
  signal_buffer_full: {
    problem: 'This agent run already holds too many unprocessed messages.',
    effect: 'The new message was not delivered; nothing was changed.',
    next: 'Wait for the agent run to process the earlier messages, then send it again.',
  },
  operation_conflict: {
    problem: 'This business operation was already claimed by another attempt.',
    effect: 'Nothing was done twice; the earlier attempt owns the operation.',
    next: 'Open the earlier attempt from the case timeline to see its result.',
  },
  operation_payload_mismatch: {
    problem: 'The same business operation was requested with different details than before.',
    effect: 'The request was refused so the earlier operation stays as recorded.',
    next: 'Check which details changed; a changed operation needs a new revision and approval.',
  },
  operation_uncertain: {
    problem: 'The outcome of this business operation is not yet known.',
    effect: 'No retry or release is allowed until the outcome is reconciled.',
    next: 'Wait for reconciliation, or ask an operator to reconcile it.',
  },
  approval_subject_rejected: {
    problem: 'This item was already rejected at this gate in its current revision.',
    effect: 'A new approval request for the same revision is refused.',
    next: 'Revise the item; a changed revision opens a new approval request.',
  },
  payload_too_large: {
    problem: 'The data sent to a workflow step was larger than the engine allows.',
    effect: 'That step did not run; nothing was changed by it.',
    next: 'Store large content as a document and pass a reference, or ask an operator about the size limit.',
  },
  not_implemented: {
    problem: 'This kind of request is declared but not available yet.',
    effect: 'Nothing was changed.',
    next: 'Use the supported path (for example a business-document approval), or ask an operator.',
  },
  // 23: model management (ADR-022).
  connection_revision_unavailable: {
    problem: 'The model connection this run was pinned to has changed or been removed.',
    effect: 'The model was not called; the platform never switches a pinned run to another endpoint.',
    next: 'Restore the old connection configuration, or start a new attempt so it binds the current one.',
  },
  model_revoked: {
    problem: 'The model was revoked by an operator.',
    effect: 'No further calls go to it, and fallback does not bypass the revocation.',
    next: 'Choose a profile whose model is approved, or ask an operator to approve the model again.',
  },
  model_binding_missing: {
    problem: 'This run started before model bindings existed and has no recorded model binding.',
    effect: 'The run was stopped instead of guessing which model it used.',
    next: 'Cancel it and start the work again; see the migration notes for model management.',
  },
  // 26a (brief 26 §6.10 copy, verbatim): added with the error codes so `Record<ErrorCode, …>` stays complete; 26c owns
  // this dictionary and keeps or refines these entries.
  obligation_unowned: {
    problem: 'Nobody is set up to answer this item.',
    effect: 'The item was not created, so the case cannot continue on its own.',
    next: 'Ask an administrator to give the role a member, then try again.',
  },
  obligation_settled: {
    problem: 'This item was already settled.',
    effect: 'Your answer was saved but not used.',
    next: 'Open the item to see the answer that counts. Use Change answer if it is wrong.',
  },
  resolution_undefined: {
    problem: 'This process does not say when its work counts as done.',
    effect: 'It cannot be published or started in strict mode.',
    next: 'Ask the process owner to declare how the outcome is checked.',
  },
  resolution_unavailable: {
    problem: 'We could not check whether the case reached its outcome.',
    effect: 'The case stays open. Nothing was closed or stopped.',
    next: 'Try again later. If it keeps failing, give the reference below to an operator.',
  },
};

/** Problem / effect / next step for an error code. Unknown codes get a neutral explanation. */
export function errorExplanation(code: ErrorCode | string): ErrorExplanation {
  return Object.hasOwn(ERRORS, code) ? ERRORS[code as ErrorCode] : ERRORS.internal;
}

/** Longest short title, in characters, before it is cut with an ellipsis. */
export const SHORT_TITLE_MAX = 80;

function clip(text: string, max = SHORT_TITLE_MAX): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Stable short title of a task (audit F10): `task.summary` when present, else the objective's first clause (text
 * before the first `:` or the end of the first sentence). Cut to 80 characters with an ellipsis. The full objective
 * stays under "Request details".
 */
export function shortTitle(task: { objective: string; summary?: string | undefined }): string {
  if (task.summary?.trim()) return clip(task.summary);
  const objective = task.objective.replace(/\s+/g, ' ').trim();
  const colon = objective.indexOf(':');
  const sentence = objective.search(/[.!?](\s|$)/);
  const ends = [colon, sentence].filter((i) => i > 0);
  const cut = ends.length ? Math.min(...ends) : objective.length;
  return clip(objective.slice(0, cut) || objective);
}

/**
 * Who or what the task is waiting for, or `null` when nothing is pending (terminal states). Says "you" only when the
 * viewer is the person who has to act.
 */
export function waitingFor(state: TaskState | string, ctx: TaskStateContext = {}): string | null {
  switch (state) {
    case 'awaiting_approval':
      return ctx.isApprover && !ctx.isRequester ? 'you or another approver' : 'an approver';
    case 'awaiting_input':
      return ctx.isRequester ? 'you' : 'the requester';
    case 'queued':
    case 'running':
      return 'the agent';
    case 'reconciling':
    case 'compensating':
      return 'the platform';
    case 'blocked':
      return 'an operator';
    default:
      return null;
  }
}

/** The first `count` sentences of a text (for two-sentence leads); the whole text when it is shorter. */
export function leadSentences(text: string, count: number): string {
  const parts = text.trim().split(/(?<=[.!?])\s+(?=[A-Z0-9"'(§€$])/);
  return parts.slice(0, count).join(' ').trim();
}
